import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Card, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';

import CallexLoader from '../../../components/CallexLoader';
import FormNumberInput from '../../../components/form/FormNumberInput';

import { vbPricingGet, vbPricingSave } from '../../../store/slices/voiceBroadcastBillingSlice';

const BLOCK_SECONDS = 15;

const CALL_TYPES = [
  { key: 'normal', label: 'Normal Voice Broadcast', fallback: '0.09' },
  { key: 'dynamic_ivr', label: 'Dynamic IVR', fallback: '0.15' }
];

// Durations shown in the live preview so the effect of the rate is visible,
// including past 60s where the price keeps scaling.
const PREVIEW_SECS = [15, 30, 45, 60, 75, 120, 300];

const blocksFor = (secs) => Math.ceil(secs / BLOCK_SECONDS);

const fmt = (n) =>
  Number.isFinite(n) ? n.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '-';

// One rate per call type is the entire card: charge = ceil(billsec/15) x rate.
// Master admin sets that one number; every duration is derived from it.
const SlabPricingModal = ({ isOpen, company, onCloseClick }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [sources, setSources] = useState({});
  const [rates, setRates] = useState({ normal: '', dynamic_ivr: '' });
  const [errors, setErrors] = useState({});

  const companyId = company?.company_id;

  const fetchPricing = async () => {
    setIsLoading(true);
    try {
      const response = await dispatch(vbPricingGet(companyId)).unwrap();
      const next = {};
      const src = {};
      (response?.pricing || []).forEach((p) => {
        src[p.call_type] = p.source;
        const base = p.rate_per_block ?? p.slab_0_15;
        next[p.call_type] = base !== undefined && base !== null ? String(base) : '';
      });
      CALL_TYPES.forEach(({ key, fallback }) => {
        if (!next[key]) next[key] = fallback;
      });
      setRates(next);
      setSources(src);
    } catch (error) {
      console.log('error', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && companyId) {
      setErrors({});
      fetchPricing();
    }
  }, [isOpen, companyId]);

  const setRate = (callType, value) => {
    setRates((prev) => ({ ...prev, [callType]: value }));
    setErrors((prev) => ({ ...prev, [callType]: undefined }));
  };

  const validate = () => {
    const next = {};
    CALL_TYPES.forEach(({ key }) => {
      const raw = rates[key];
      if (raw === '' || raw === null || raw === undefined) {
        next[key] = 'Required';
      } else if (!Number.isFinite(Number(raw)) || Number(raw) < 0) {
        next[key] = 'Must be >= 0';
      }
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSave = async () => {
    if (!validate()) {
      toast.error('Please enter a valid rate for each call type');
      return;
    }
    setIsSaving(true);
    try {
      // One request per call type; the endpoint upserts on (company_id, call_type)
      // and derives the display slabs from this single rate.
      for (const { key, label } of CALL_TYPES) {
        const response = await dispatch(
          vbPricingSave({
            company_id: Number(companyId),
            call_type: key,
            slab_0_15: Number(rates[key])
          })
        ).unwrap();
        if (!response?.success) {
          toast.error(`Could not save ${label} pricing`);
          setIsSaving(false);
          return;
        }
      }
      toast.success('Pricing saved successfully');
      onCloseClick(true);
    } catch (error) {
      console.log('error', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} size="lg" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">Pricing — {company?.company_name || `Company #${companyId}`}</div>
      </ModalHeader>
      <ModalBody className="px-4">
        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: 260 }}>
            <CallexLoader />
          </div>
        ) : (
          <>
            <div className="mb-3" style={{ fontSize: '13px' }}>
              Set the charge for one <strong>{BLOCK_SECONDS}-second block</strong>. The price scales
              with call length automatically — a call is charged{' '}
              <code>
                ceil(answered seconds / {BLOCK_SECONDS}) × rate
              </code>
              . Billing uses <strong>answered</strong> seconds, so unanswered calls are never
              charged.
            </div>

            <Row>
              {CALL_TYPES.map(({ key, label }) => {
                const base = Number(rates[key]);
                const valid = Number.isFinite(base) && base >= 0;
                return (
                  <Col lg="6" key={key}>
                    <Card className="p-3 mb-0 h-100">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <strong>{label}</strong>
                        {sources[key] === 'default' && (
                          <span className="text-warning" style={{ fontSize: '12px' }}>
                            using default
                          </span>
                        )}
                      </div>

                      <FormNumberInput
                        name={key}
                        label={`Charge per ${BLOCK_SECONDS} seconds (Rs)`}
                        placeholder="0.00"
                        min={0}
                        step="0.01"
                        value={rates[key]}
                        onChange={(e) => setRate(key, e.target.value)}
                        touched={{ [key]: !!errors[key] }}
                        errors={errors}
                      />

                      <div className="mt-1" style={{ fontSize: '12px' }}>
                        <div className="text-muted mb-1">Resulting charges</div>
                        <table className="w-100">
                          <tbody>
                            {PREVIEW_SECS.map((s) => (
                              <tr key={s}>
                                <td className="text-muted">
                                  {s}s
                                  <span className="ms-1" style={{ opacity: 0.6 }}>
                                    ({blocksFor(s)} × {BLOCK_SECONDS}s)
                                  </span>
                                </td>
                                <td className="text-end fw-bold">
                                  {valid ? fmt(blocksFor(s) * base) : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button onClick={() => onCloseClick()} className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button
          onClick={onSave}
          disabled={isSaving || isLoading}
          className="px-5 bg-primary border-primary">
          {isSaving ? 'Saving...' : 'Save Pricing'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

SlabPricingModal.propTypes = {
  isOpen: PropTypes.bool,
  company: PropTypes.any,
  onCloseClick: PropTypes.func
};

export default SlabPricingModal;
